import COMPATIO_LOGO from 'assests/img/compatio_logo.png';
import React from 'react';
import { formatDateTime } from '../utils/Helper';
import { BlockRightArrowIcon } from 'assests/GmsIcons';

type TRecategorizationWrapperProps = {
    sourceName: string
    targetName: string
    totalProducts: number
    attributes: TOption[]
}

/**
 * The downloadable receipt — and the ONE file in this flow whose colour literals are
 * correct and must stay literal.
 *
 * `RecategorizationWrapper` renders this off-screen and feeds it to `html2canvas` at
 * `backgroundColor: '#ffffff'`. html2canvas reads COMPUTED styles, so a `var(--ds-*)`
 * here would resolve against whatever theme the operator happens to be using and a
 * dark-mode user would download a PDF printed in near-white ink on a white page.
 *
 * The receipt is a paper artefact: it has one appearance, and it is the light one.
 * Every text node below therefore states its own colour explicitly — verified, none
 * inherits — so the page ignores `data-theme` entirely.
 *
 * The root states `tw-text-[#172b4d]` for the same reason: `BlockRightArrowIcon`
 * draws in `currentColor` and nothing between it and <body> set one, so after § 274
 * it inherited `--ds-ink` — a near-WHITE arrow painted onto the white page for any
 * operator in dark mode. The root ink pins it, and covers anything added later.
 *
 * Do not "finish the migration" by tokenizing this file.
 */
const RecategorizationPDF = ({ sourceName, targetName, totalProducts, attributes }: TRecategorizationWrapperProps) => {
    return (<div className="tw-p-8 tw-bg-white tw-text-[#172b4d]" style={{ width: '20cm', minHeight: '27.7cm' }}>
        <div className="tw-mb-6">
            <div className="tw-flex tw-justify-end tw-mb-6">
                <img
                    src={COMPATIO_LOGO}
                    alt="Compatio Logo"
                    className="tw-w-[120px] tw-h-[35px]"
                    crossOrigin="anonymous"
                />
            </div>
            <h1 className=" tw-text-[#172b4d]  tw-mb-2 tw-text-xl tw-font-semibold">
                Recategorization
            </h1>
            <p className="tw-text-[#172b4d] tw-text-sm tw-font-semibold">
                {formatDateTime()}
            </p>
        </div>
        <div className="tw-mb-6">
            <label className="tw-text-sm tw-font-semibold tw-text-[#969fae] tw-block tw-mb-3">
                Number of Moved Products
            </label>
            <div className="tw-flex tw-items-center">
                <span className="tw-bg-[#F4F5F7] tw-rounded-md tw-w-[120px] tw-border tw-border-[#D1D1D6] tw-px-4 tw-text-base tw-font-semibold tw-pb-3 tw-text-[#172b4d] tw-block">
                    {totalProducts}
                </span>
            </div>
        </div>

        <div className="tw-flex tw-justify-between tw-gap-8 tw-mb-6">
            <div className="tw-flex-1">
                <label className="tw-text-sm tw-font-semibold tw-text-[#969fae] tw-block tw-mb-3">
                    From (Source Category)
                </label>
                <div className=" tw-flex tw-items-center">
                    <span className="tw-bg-[#F4F5F7] tw-rounded-md tw-w-full tw-h-auto tw-min-h-[40px] tw-pb-4 tw-px-4 tw-border tw-border-[#D1D1D6] tw-text-sm tw-font-semibold tw-text-[#172B4D]">
                        {sourceName}
                    </span>
                </div>
            </div>

            <div className='tw-mt-10'>
                <BlockRightArrowIcon />
            </div>
            <div className="tw-flex-1">
                <label className="tw-text-sm tw-font-semibold tw-text-[#969fae] tw-block tw-mb-3">
                    To (Target Category)
                </label>
                <div className=" tw-flex tw-items-center">
                    <span className="tw-bg-[#F4F5F7] tw-h-auto tw-min-h-[40px] tw-pb-4 tw-w-full tw-rounded-md tw-border tw-px-4 tw-border-[#D1D1D6]  tw-text-sm tw-font-semibold tw-text-[#172B4D]">
                        {targetName}
                    </span>
                </div>
            </div>
        </div>

        <div className="tw-w-auto h-[0px] border border-[#dfe0e6] tw-mb-6"></div>

        <div className="tw-mb-12">
            <h2 className="tw-text-base tw-text-[#172b4d] tw-font-semibold tw-mb-6">
                Ensure these actions are completed to finalize the <br />recategorization process -
            </h2>

            <div className="tw-space-y-6">
                <div>
                    <p className=" tw-text-[#03B3FF] tw-mb-4 tw-text-sm tw-font-semibold">
                        1. Insert the required Attribute Values for the below attributes
                    </p>
                    <div className="tw-bg-[#F4F5F7] tw-rounded-md tw-border tw-border-[#D1D1D6] tw-p-6">
                        <div className='tw-w-auto tw-mb-2'>
                            {attributes?.map((attribute, index) =>
                                <div key={index} className="tw-inline-flex tw-m-1">
                                    <span className="tw-bg-[#DADBDD] tw-rounded-sm tw-px-1 tw-text-sm tw-text-center tw-pb-3 tw-text-[#172b4d] tw-font-normal">
                                        {attribute?.name}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <p className=" tw-text-[#03B3FF] tw-mb-4 tw-text-sm tw-font-semibold">
                    2. Create a CBM rule for the recategorized products
                </p>

                <p className=" tw-text-[#03B3FF] tw-mb-4 tw-text-sm tw-font-semibold">
                    3. Check the Rule Queue for Completed status and rerun manually if needed
                </p>
            </div>
        </div>
        <div className="tw-absolute tw-bottom-8 tw-left-8 tw-right-8">
            <span className="tw-text-[#6d788c] tw-text-[10px] tw-font-semibold tw-font-['Noto Sans'] tw-leading-tight tw-tracking-wide">
                * This document is for authorized users only. Unauthorized access, distribution, or misuse is strictly prohibited and may result in disciplinary action.
            </span>
            <span className="tw-text-[#172b4d] tw-ml-1 tw-text-[10px] tw-font-semibold tw-font-['Noto Sans'] tw-leading-tight tw-tracking-wide">
                For more information visit
            </span>
            <a href='www.compatio.ai' className="tw-text-[#03b3ff] tw-ml-1 tw-text-[10px] tw-font-semibold tw-font-['Noto Sans'] tw-leading-tight tw-tracking-wide">
                www.compatio.ai
            </a>
        </div>
    </div>)
}

export default RecategorizationPDF;